import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom'
import AuthLayout from './layouts/AuthLayout'
import AppLayout from './layouts/AppLayout'
import ConsoleShell from './layouts/ConsoleShell'
import PublicLayout from './layouts/PublicLayout'
import CookieBanner from './components/CookieBanner'
import { useAnalytics } from './hooks/useAnalytics'
import { GlossaryProvider } from './components/educational/GlossaryContext'
import { ImpersonationBanner } from './components/ImpersonationBanner'
import ProtectedRoute from './components/ProtectedRoute'

/**
 * Reindirizza una scheda contatto dal vecchio indirizzo admin al pannello CRM & Marketing.
 * `<Navigate>` da solo non basta: il percorso porta un parametro, e una stringa fissa
 * manderebbe tutti sullo stesso contatto — silenziosamente, il che è peggio di un 404.
 */
function RedirectContact({ edit }: { edit?: boolean }) {
  const { id } = useParams()
  return <Navigate to={`/app/crm-marketing/crm/contacts/${id}${edit ? '/edit' : ''}`} replace />
}

// ── Lazy page imports ──────────────────────────────────────────────────────────
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'))
const ContributeSpeciesMediaWizard = lazy(() => import('./pages/species/ContributeSpeciesMediaWizard'))
const LandingPage = lazy(() => import('./pages/landing/LandingPage'))
const OnboardingWizard = lazy(() => import('./pages/onboarding/OnboardingWizard'))
const GlossaryPage = lazy(() => import('./pages/glossary/GlossaryPage'))
const DiveDetailPage = lazy(() => import('./pages/logbook/DiveDetailPage'))
const GDPRPage = lazy(() => import('./pages/gdpr/GDPRPage'))
const ReferralPage = lazy(() => import('./pages/referral/ReferralPage'))
const MyNetworkPage = lazy(() => import('./pages/referral/MyNetworkPage'))
const AdminReferralTreePage = lazy(() => import('./pages/admin/AdminReferralTreePage'))
const AchievementsPage = lazy(() => import('./pages/achievements/AchievementsPage'))
const WalletPage = lazy(() => import('./pages/wallet/WalletPage'))
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'))
const OAuthCallbackPage = lazy(() => import('./pages/auth/OAuthCallbackPage'))
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage'))
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'))
const LogbookPage = lazy(() => import('./pages/logbook/LogbookPage'))
const GasDecoPage = lazy(() => import('./pages/logbook/GasDecoPage'))
const DivesitesPage = lazy(() => import('./pages/divesites/DivesitesPage'))
const CoursesPage = lazy(() => import('./pages/courses/CoursesPage'))
const FormazionePage = lazy(() => import('./pages/training/FormazionePage'))
const DivingHubPage = lazy(() => import('./pages/diving/DivingHubPage'))
const ServicesHubPage = lazy(() => import('./pages/services/ServicesHubPage'))
const ServicesCategoryPage = lazy(() => import('./pages/services/ServicesCategoryPage'))
const ServiceDetailPage = lazy(() => import('./pages/services/ServiceDetailPage'))
const CourseLessonsPage = lazy(() => import('./pages/courses/CourseLessonsPage'))
const ExamPage = lazy(() => import('./pages/courses/ExamPage'))
const PSSExamPage = lazy(() => import('./pages/courses/PSSExamPage'))
const PlanningPage = lazy(() => import('./pages/planning/PlanningPage'))
const EventsPage = lazy(() => import('./pages/events/EventsPage'))
const QAPage = lazy(() => import('./pages/qa/QAPage'))
const QuestionPage = lazy(() => import('./pages/qa/QuestionPage'))
const SocialPage = lazy(() => import('./pages/social/SocialPage'))
const ClipUploadPage = lazy(() => import('./pages/live/ClipUploadPage'))
const AdminClipsPage = lazy(() => import('./pages/admin/AdminClipsPage'))
const ChatPage = lazy(() => import('./pages/chat/ChatPage'))
const BookingsPage = lazy(() => import('./pages/bookings/BookingsPage'))
const InsurancePage = lazy(() => import('./pages/insurance/InsurancePage'))
const MediaPage = lazy(() => import('./pages/media/MediaPage'))
const MediaCorrectionPage = lazy(() => import('./pages/media/MediaCorrectionPage'))
const PhotographyHubPage = lazy(() => import('./pages/photography/PhotographyHubPage'))
const ManualiPage = lazy(() => import('./pages/help/ManualiPage'))
const MedicalPage = lazy(() => import('./pages/medical/MedicalPage'))
const MedicalQuestionnairePage = lazy(() => import('./pages/medical/MedicalQuestionnairePage'))
const FitnessAssessmentsPage = lazy(() => import('./pages/medical/FitnessAssessmentsPage'))
const CylinderCertificationsPage = lazy(() => import('./pages/profile/CylinderCertificationsPage'))
const DiveDoctorPage = lazy(() => import('./pages/medical/DiveDoctorPage'))
const FindMedicalPage = lazy(() => import('./pages/medical/FindMedicalPage'))
const AdminPage = lazy(() => import('./pages/admin/AdminPage'))
const AdminHubPage = lazy(() => import('./pages/admin/AdminHubPage'))
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'))
const MarketingPage = lazy(() => import('./pages/admin/MarketingPage'))
const MarineBiologistPortal = lazy(() => import('./pages/species/MarineBiologistPortal'))
const BulkUploadConsolePage = lazy(() => import('./pages/species/BulkUploadConsolePage'))
const MarineGalleryPage = lazy(() => import('./pages/marine/MarineGalleryPage'))
const MarineMediaDetailPage = lazy(() => import('./pages/marine/MarineMediaDetailPage'))

const BiologyCenterDashboard = lazy(() => import('./pages/biology-center/BiologyCenterDashboard'))
const MissionsPage = lazy(() => import('./pages/biology-center/MissionsPage'))

const ResearchAnalyticsPage = lazy(() => import('./pages/biology-center/ResearchAnalyticsPage'))
const TaxonomyExplorerPage = lazy(() => import('./pages/biology-center/TaxonomyExplorerPage'))
const MarineBioAssistantPage = lazy(() => import('./pages/biology-center/MarineBioAssistantPage'))
const SpeciesIdentifierPage = lazy(() => import('./pages/biology-center/SpeciesIdentifierPage'))
const BioCenterHubPage = lazy(() => import('./pages/biology-center/BioCenterHubPage'))
const FieldJournalPage = lazy(() => import('./pages/biology-center/FieldJournalPage'))
const SpeciesReportPage = lazy(() => import('./pages/biology-center/SpeciesReportPage'))
const MissionDetailPage = lazy(() => import('./pages/biology-center/MissionDetailPage'))
const MissionResultsPage = lazy(() => import('./pages/biology-center/MissionResultsPage'))
const MarineProjectsPage = lazy(() => import('./pages/biology-center/MarineProjectsPage'))
const MarineProjectDetailPage = lazy(() => import('./pages/biology-center/MarineProjectDetailPage'))
const MarineDiveDetailPage = lazy(() => import('./pages/biology-center/MarineDiveDetailPage'))
const AlertsPage = lazy(() => import('./pages/biology-center/AlertsPage'))
const SpeciesEditorPage = lazy(() => import('./pages/biology-center/SpeciesEditorPage'))
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'))
const CertCardPage = lazy(() => import('./pages/profile/CertCardPage'))
const CheckInQRPage = lazy(() => import('./pages/profile/CheckInQRPage'))
const CareerPage = lazy(() => import('./pages/profile/CareerPage'))
const DiveCenterLayout = lazy(() => import('./pages/divecenter/DiveCenterLayout'))
const DCBookingsPage = lazy(() => import('./pages/divecenter/DCBookingsPage'))
const DCCustomersPage = lazy(() => import('./pages/divecenter/DCCustomersPage'))
const DCPlanningPage = lazy(() => import('./pages/divecenter/DCPlanningPage'))
const DCCalendarPage = lazy(() => import('./pages/divecenter/DCCalendarPage'))
const DCEquipmentPage = lazy(() => import('./pages/divecenter/DCEquipmentPage'))
const DCBoatsPage = lazy(() => import('./pages/divecenter/DCBoatsPage'))
const DCEquipmentMaintenancePage = lazy(() => import('./pages/divecenter/DCEquipmentMaintenancePage'))
const DCEquipmentItemDetailPage = lazy(() => import('./pages/divecenter/DCEquipmentItemDetailPage'))
const DCOperationsPage = lazy(() => import('./pages/divecenter/DCOperationsPage'))
const DCLogbookPage = lazy(() => import('./pages/divecenter/DCLogbookPage'))
const DCCoursesPage = lazy(() => import('./pages/divecenter/DCCoursesPage'))
const DCWaiverSignPage = lazy(() => import('./pages/divecenter/DCWaiverSignPage'))
const DCStudentProgressPage = lazy(() => import('./pages/divecenter/DCStudentProgressPage'))
const DCBoatSheetPage = lazy(() => import('./pages/divecenter/DCBoatSheetPage'))
const DCCourseTeamPage = lazy(() => import('./pages/divecenter/DCCourseTeamPage'))
const DCAdminPage = lazy(() => import('./pages/divecenter/DCAdminPage'))
const DCMarketingPage = lazy(() => import('./pages/divecenter/DCMarketingPage'))
const DCDashboardPage = lazy(() => import('./pages/divecenter/DCDashboardPage'))
const DCAnalyticsPage = lazy(() => import('./pages/divecenter/DCAnalyticsPage'))
const DCCompliancePage = lazy(() => import('./pages/divecenter/DCCompliancePage'))
const DCCustomerNotesPage = lazy(() => import('./pages/divecenter/DCCustomerNotesPage'))
const DCHRPage = lazy(() => import('./pages/divecenter/DCHRPage'))
const DCReportsPage = lazy(() => import('./pages/divecenter/DCReportsPage'))
const DCRentalPage = lazy(() => import('./pages/divecenter/DCRentalPage'))
const DCWidgetPage = lazy(() => import('./pages/divecenter/DCWidgetPage'))
const SubscriptionPage = lazy(() => import('./pages/profile/SubscriptionPage'))
const DCInvoicesPage = lazy(() => import('./pages/divecenter/DCInvoicesPage'))
const DCPricingPage = lazy(() => import('./pages/divecenter/DCPricingPage'))
const DCShiftsPage = lazy(() => import('./pages/divecenter/DCShiftsPage'))
const DivemasterProductivityPage = lazy(() => import('./pages/divecenter/DivemasterProductivityPage'))
const AcademyDashboardPage = lazy(() => import('./pages/academy/AcademyDashboardPage'))
const ResearchCenterPage = lazy(() => import('./pages/research-center/ResearchCenterPage'))
const DiveCenterProfilePage = lazy(() => import('./pages/divecenter/DiveCenterProfilePage'))
const UnifiedDiveCenterPage = lazy(() => import('./pages/divecenter/UnifiedDiveCenterPage'))
const AssociationsPage = lazy(() => import('./pages/associations/AssociationsPage'))
const TourismPage = lazy(() => import('./pages/tourism/TourismPage'))
const TourismExcursionsPage = lazy(() => import('./pages/tourism/ExcursionsPage'))
const MarketplacePage = lazy(() => import('./pages/marketplace/MarketplacePage'))
const MerchShopPage = lazy(() => import('./pages/merch/MerchShopPage'))
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'))
const ContentPage = lazy(() => import('./pages/content/ContentPage'))
const NewsReaderPage = lazy(() => import('./pages/news/NewsReaderPage'))
const AIPage = lazy(() => import('./pages/ai/AIPage'))
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage'))
const SafetyPage = lazy(() => import('./pages/safety/SafetyPage'))
// UnifiedEquipmentPage removed — equipment management lives in /app/profile?tab=attrezzatura
const UnifiedSpeciesPage = lazy(() => import('./pages/species/UnifiedSpeciesPage'))
const AssociationShopPage = lazy(() => import('./pages/associations/AssociationShopPage'))
const InstructorDashboardPage = lazy(() => import('./pages/instructor/InstructorDashboardPage'))
const InstructorPublicProfilePage = lazy(() => import('./pages/instructor/InstructorPublicProfilePage'))
const InstructorDirectoryPage = lazy(() => import('./pages/instructor/InstructorDirectoryPage'))
const DiveSiteDirectoryPage = lazy(() => import('./pages/directory/DiveSiteDirectoryPage'))
const UnifiedDirectorySearchPage = lazy(() => import('./pages/directory/UnifiedDirectorySearchPage'))
const AnagraficaWorkspacePage = lazy(() => import('./pages/directory/AnagraficaWorkspacePage'))
const PartyOrganizationDetailPage = lazy(() => import('./pages/directory/PartyOrganizationDetailPage'))
const PersonSearchPage = lazy(() => import('./pages/directory/PersonSearchPage'))
const PartyPersonDetailPage = lazy(() => import('./pages/directory/PartyPersonDetailPage'))
const PartyPersonByUserPage = lazy(() => import('./pages/directory/PartyPersonByUserPage'))
const PrivateLessonsPage = lazy(() => import('./pages/instructor/PrivateLessonsPage').then(m => ({ default: m.PrivateLessonsPage })))
const MyCertificationsPage = lazy(() => import('./pages/instructor/MyCertificationsPage'))
const MyStaffRequestsPage = lazy(() => import('./pages/instructor/MyStaffRequestsPage'))
const DCMedCertRequestsPage = lazy(() => import('./pages/divecenter/DCMedCertRequestsPage').then(m => ({ default: m.DCMedCertRequestsPage })))
const EventInvitationPage = lazy(() => import('./pages/events/EventInvitationPage'))
const DiveCheckInPage = lazy(() => import('./pages/safety/DiveCheckInPage'))
const PublicDiverProfilePage = lazy(() => import('./pages/profile/PublicDiverProfilePage'))
const ContactRequestsPage = lazy(() => import('./pages/contacts/ContactRequestsPage'))
const ContactsPage = lazy(() => import('./pages/contacts/ContactsPage'))
const StudentCoursesPage = lazy(() => import('./pages/student/StudentCoursesPage'))
const JobBoardPage = lazy(() => import('./pages/jobs/JobBoardPage'))
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'))
const LogbookAnalyticsPage = lazy(() => import('./pages/logbook/LogbookAnalyticsPage'))
const TripReportPage = lazy(() => import('./pages/logbook/TripReportPage'))
const DiveComparePage = lazy(() => import('./pages/logbook/DiveComparePage'))
const ProfileAIInsightsPage = lazy(() => import('./pages/logbook/ProfileAIInsightsPage'))
const AIDiveBriefingPage = lazy(() => import('./pages/planning/AIDiveBriefingPage'))
const PlanDiveWizard = lazy(() => import('./pages/planning/PlanDiveWizard'))
const PlanningToolsPage = lazy(() => import('./pages/planning/PlanningToolsPage'))
const ProfileSetupWizard = lazy(() => import('./pages/profile/ProfileSetupWizard'))
const MedicalWizard = lazy(() => import('./pages/medical/MedicalWizard'))
const DoctorOnboardingWizard = lazy(() => import('./pages/medical/DoctorOnboardingWizard'))
const DiveCenterOnboardingWizard = lazy(() => import('./pages/divecenter/DiveCenterOnboardingWizard'))
const InstructorOnboardingWizard = lazy(() => import('./pages/instructor/InstructorOnboardingWizard'))
const DivemasterOnboardingWizard = lazy(() => import('./pages/diver/DivemasterOnboardingWizard'))
const AgencyOnboardingWizard = lazy(() => import('./pages/agency/AgencyOnboardingWizard'))
const AssociationOnboardingWizard = lazy(() => import('./pages/associations/AssociationOnboardingWizard'))
const AcademyOnboardingWizard = lazy(() => import('./pages/academy/AcademyOnboardingWizard'))
const PhotographerOnboardingWizard = lazy(() => import('./pages/photographer/PhotographerOnboardingWizard'))
const ResearchCenterOnboardingWizard = lazy(() => import('./pages/biology-center/ResearchCenterOnboardingWizard'))
const LogDiveWizard = lazy(() => import('./pages/logbook/LogDiveWizard'))
const ExportLogbookWizard = lazy(() => import('./pages/logbook/ExportLogbookWizard'))
const CourseEnrollWizard = lazy(() => import('./pages/courses/CourseEnrollWizard'))
const BookDiveWizard = lazy(() => import('./pages/bookings/BookDiveWizard'))
const AddCertificationWizard = lazy(() => import('./pages/profile/AddCertificationWizard'))
const AddEquipmentWizard = lazy(() => import('./pages/profile/AddEquipmentWizard'))
const FindBuddyWizard = lazy(() => import('./pages/social/FindBuddyWizard'))
const DiveComputerDirectoryPage = lazy(() => import('./pages/logbook/DiveComputerDirectoryPage'))
const ChallengesPage = lazy(() => import('./pages/community/ChallengesPage'))
const DiveImportWizard = lazy(() => import('./pages/logbook/DiveImportWizard'))
const CommunityPage = lazy(() => import('./pages/community/CommunityPage'))
const PollsPage = lazy(() => import('./pages/community/PollsPage'))
const FunzionalitaPage = lazy(() => import('./pages/public/FunzionalitaPage'))
const PublicDiveSitesPage = lazy(() => import('./pages/public/PublicDiveSitesPage'))
const PublicDiveSiteDetailPage = lazy(() => import('./pages/public/PublicDiveSiteDetailPage'))
const PublicArticlePage = lazy(() => import('./pages/public/PublicArticlePage'))
const PublicSpeciesPage = lazy(() => import('./pages/public/PublicSpeciesPage'))
const PublicSpeciesDetailPage = lazy(() => import('./pages/public/PublicSpeciesDetailPage'))
const SpeciesQuizPage = lazy(() => import('./pages/games/SpeciesQuizPage'))
const PublicCentersPage = lazy(() => import('./pages/public/PublicCentersPage'))
const PublicCenterDetailPage = lazy(() => import('./pages/public/PublicCenterDetailPage'))
const PerIstruttoriPage = lazy(() => import('./pages/public/PerIstruttoriPage'))
const PerCentriPage = lazy(() => import('./pages/public/PerCentriPage'))
const CampagnaLazioPage = lazy(() => import('./pages/public/CampagnaLazioPage'))
const PrezziPage = lazy(() => import('./pages/public/PrezziPage'))
const ChiSiamoPage = lazy(() => import('./pages/public/ChiSiamoPage'))
const BlogPage = lazy(() => import('./pages/public/BlogPage'))
const PartnerPage = lazy(() => import('./pages/public/PartnerPage'))
const ContattiPage = lazy(() => import('./pages/public/ContattiPage'))
const PrivacyPolicyPage = lazy(() => import('./pages/public/PrivacyPolicyPage'))
const TerminiServizioPage = lazy(() => import('./pages/public/TerminiServizioPage'))
const CookiePolicyPage = lazy(() => import('./pages/public/CookiePolicyPage'))
const GdprInfoPage = lazy(() => import('./pages/public/GdprInfoPage'))
const DataDeletionPage = lazy(() => import('./pages/public/DataDeletionPage'))
const UnsubscribePage  = lazy(() => import('./pages/UnsubscribePage'))
const BriefingCreatorPage = lazy(() => import('./pages/divecenter/BriefingCreatorPage'))
const BriefingSharePage = lazy(() => import('./pages/divecenter/BriefingSharePage'))
const AdvisorPage = lazy(() => import('./pages/ai/AdvisorPage'))
const LeaderboardPage = lazy(() => import('./pages/social/LeaderboardPage'))
const DiveSquadsPage = lazy(() => import('./pages/social/DiveSquadsPage'))
const DeveloperPage = lazy(() => import('./pages/developer/DeveloperPage'))
const DataLabPage = lazy(() => import('./pages/datalab/DataLabPage'))
const BuddyMatchesPage = lazy(() => import('./pages/social/BuddyMatchesPage'))
const SearchPage = lazy(() => import('./pages/search/SearchPage'))
const WishlistPage = lazy(() => import('./pages/divesites/WishlistPage'))
const NearMePage = lazy(() => import('./pages/divesites/NearMePage'))
const RecommendationsPage = lazy(() => import('./pages/divesites/RecommendationsPage'))
const MPAZonesPage = lazy(() => import('./pages/divesites/MPAZonesPage'))
const CertPrerequisitesPage = lazy(() => import('./pages/logbook/CertPrerequisitesPage'))
const GoalsPage = lazy(() => import('./pages/logbook/GoalsPage'))
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'))
const TwoFactorPage = lazy(() => import('./pages/settings/TwoFactorPage'))
const SafetyRegisterPage = lazy(() => import('./pages/divecenter/SafetyRegisterPage'))
const PublicDivePage = lazy(() => import('./pages/public/PublicDivePage'))
const TripPlannerPage = lazy(() => import('./pages/trips/TripPlannerPage'))
const AcceptInvitePage = lazy(() => import('./pages/trips/TripPlannerPage').then(m => ({ default: m.AcceptInvitePage })))
const SportCentersPage = lazy(() => import('./pages/sportcenters/SportCentersPage'))
const SportCenterDetailPage = lazy(() => import('./pages/sportcenters/SportCenterDetailPage'))
const MySportCenterPage = lazy(() => import('./pages/sportcenters/MySportCenterPage'))
const TravelDiariesPage = lazy(() => import('./pages/diaries/TravelDiariesPage'))
const DiaryDetailPage = lazy(() => import('./pages/diaries/DiaryDetailPage'))
const DiaryInvitePage = lazy(() => import('./pages/diaries/DiaryInvitePage'))
const DiaryPublicPage = lazy(() => import('./pages/diaries/DiaryPublicPage'))
const ApneaPage = lazy(() => import('./pages/apnea/ApneaPage'))
const DiverTrainingPage = lazy(() => import('./pages/diver/DiverTrainingPage'))
const DoctorPage = lazy(() => import('./pages/doctor/DoctorPage'))
const InstallPage = lazy(() => import('./pages/install/InstallPage'))
const MyAgencyPage = lazy(() => import('./pages/agency/MyAgencyPage'))
const AgencyPackagesPage = lazy(() => import('./pages/agency/AgencyPackagesPage'))
const AgencyBookingsPage = lazy(() => import('./pages/agency/AgencyBookingsPage'))
const MyTravelBookingsPage = lazy(() => import('./pages/agency/MyTravelBookingsPage'))
const MyCharterPage = lazy(() => import('./pages/charter/MyCharterPage'))
const DoctorProfilePage = lazy(() => import('./pages/medical/DoctorProfilePage'))
const DiveAuthorizationPage = lazy(() => import('./pages/divecenter/DiveAuthorizationPage'))
const ConsentResultPage = lazy(() => import('./pages/marketing/ConsentResultPage'))
const WhatsAppAdminPage = lazy(() => import('./pages/admin/WhatsAppAdminPage'))
const AdminEmailPage = lazy(() => import('./pages/admin/AdminEmailPage'))
const AdminGdprSagasPage = lazy(() => import('./pages/admin/AdminGdprSagasPage'))
const AdminGdprProcessingRecordsPage = lazy(() => import('./pages/admin/AdminGdprProcessingRecordsPage'))
const AdminGdprRetentionPage = lazy(() => import('./pages/admin/AdminGdprRetentionPage'))
const AdminJurisdictionRulesPage = lazy(() => import('./pages/admin/AdminJurisdictionRulesPage'))
const AdminCommsPage = lazy(() => import('./pages/admin/AdminCommsPage'))
const AdminContentPagesPage = lazy(() => import('./pages/admin/AdminContentPagesPage'))
const AdminRbacPolicyPage = lazy(() => import('./pages/admin/AdminRbacPolicyPage'))
const AdminRulesPage = lazy(() => import('./pages/admin/AdminRulesPage'))
const BadgeVisibilityPage = lazy(() => import('./pages/admin/BadgeVisibilityPage'))
const AdminNewsPage = lazy(() => import('./pages/admin/AdminNewsPage'))
const DirectoryPage = lazy(() => import('./pages/admin/directory/DirectoryPage'))
const MailPage = lazy(() => import('./pages/admin/mail/MailPage'))
const AdminDataLabPage = lazy(() => import('./pages/admin/AdminDataLabPage'))
const AdminInatTokenPage = lazy(() => import('./pages/admin/AdminInatTokenPage'))
const AdminIntegrationsPage = lazy(() => import('./pages/admin/AdminIntegrationsPage'))
const AdminServicesPage = lazy(() => import('./pages/admin/AdminServicesPage'))
const AdminChallengesPage = lazy(() => import('./pages/admin/AdminChallengesPage'))
const AdminRoleRequestsPage = lazy(() => import('./pages/admin/AdminRoleRequestsPage'))
const AdminDoctorVerificationPage = lazy(() => import('./pages/admin/AdminDoctorVerificationPage'))
const AdminTourOperatorsPage = lazy(() => import('./pages/admin/AdminTourOperatorsPage'))
const AdminCertificationsPage = lazy(() => import('./pages/admin/AdminCertificationsPage'))
const AdminStatsPage = lazy(() => import('./pages/admin/AdminStatsPage'))
const AdminPSSPage = lazy(() => import('./pages/admin/AdminPSSPage'))
const AdminMpaPage = lazy(() => import('./pages/admin/AdminMpaPage'))
const AdminPromotionsPage = lazy(() => import('./pages/admin/AdminPromotionsPage'))
const DiveSharePage = lazy(() => import('./pages/logbook/DiveSharePage'))
const GasPlannerPage = lazy(() => import('./pages/logbook/GasPlannerPage'))
const PhotographerPortalPage = lazy(() => import('./pages/photographer/PhotographerPortalPage'))
const ProfessionalsDirectoryPage = lazy(() => import('./pages/professionals/ProfessionalsDirectoryPage'))
const ExcursionDetailPage = lazy(() => import('./pages/events/ExcursionDetailPage'))
const ExcursionsPage = lazy(() => import('./pages/events/ExcursionsPage'))
const DCExcursionsPage = lazy(() => import('./pages/events/DCExcursionsPage'))
const DCExcursionDetailPage = lazy(() => import('./pages/divecenter/DCExcursionDetailPage'))
const MedicalHistoryPage = lazy(() => import('./pages/profile/MedicalHistoryPage'))
const EmergencyQRPage = lazy(() => import('./pages/profile/EmergencyQRPage'))
const EmergencyMedicalVerifyPage = lazy(() => import('./pages/profile/EmergencyMedicalVerifyPage'))
const CertWalletPage = lazy(() => import('./pages/profile/CertWalletPage'))
const CertWalletVerifyPage = lazy(() => import('./pages/profile/CertWalletVerifyPage'))
const CertVerifyPage = lazy(() => import('./pages/profile/CertVerifyPage'))
const AdminPartyPage = lazy(() => import('./pages/admin/AdminPartyPage'))
const CrmDashboardPage = lazy(() => import('./pages/admin/crm/CrmDashboardPage'))
const CrmContactFormPage = lazy(() => import('./pages/admin/crm/CrmContactFormPage'))
const CrmContactDetailPage = lazy(() => import('./pages/admin/crm/CrmContactDetailPage'))
const CrmAgendaPage = lazy(() => import('./pages/admin/crm/CrmAgendaPage'))
const CrmTodosPage = lazy(() => import('./pages/admin/crm/CrmTodosPage'))
const CrmOsmImportPage = lazy(() => import('./pages/admin/crm/CrmOsmImportPage'))
const CrmEnrichPage = lazy(() => import('./pages/admin/crm/CrmEnrichPage'))
const CrmOutreachPage = lazy(() => import('./pages/admin/crm/CrmOutreachPage'))
const CrmMarketingHubPage = lazy(() => import('./pages/crm-marketing/CrmMarketingHubPage'))
const AdminMerchPage = lazy(() => import('./pages/admin/AdminMerchPage'))
const AdminEquipmentCataloguePage = lazy(() => import('./pages/admin/AdminEquipmentCataloguePage'))
const AdminEquipmentOverduePage = lazy(() => import('./pages/admin/AdminEquipmentOverduePage'))
const JoinMissionWizard = lazy(() => import('./pages/biology-center/JoinMissionWizard'))
const CreateExcursionWizard = lazy(() => import('./pages/divecenter/CreateExcursionWizard'))
const PrivacyWizard = lazy(() => import('./pages/gdpr/PrivacyWizard'))
const MedicalAssessmentWizard = lazy(() => import('./pages/medical/MedicalAssessmentWizard'))
const CreateSquadWizard = lazy(() => import('./pages/social/CreateSquadWizard'))
const TravelPackageWizard = lazy(() => import('./pages/trips/TravelPackageWizard'))
const CreateDiaryWizard = lazy(() => import('./pages/diaries/CreateDiaryWizard'))
const KycWizardRaw = lazy(() => import('./pages/kyc/KycWizard'))
const RequestRoleWizard = lazy(() => import('./pages/profile/RequestRoleWizard'))
const InviteStaffWizard = lazy(() => import('./pages/divecenter/InviteStaffWizard'))
const CreateCourseWizard = lazy(() => import('./pages/divecenter/CreateCourseWizard'))
const SafetyCheckInWizard = lazy(() => import('./pages/safety/SafetyCheckInWizard'))
const IssueCertificateWizard = lazy(() => import('./pages/divecenter/IssueCertificateWizard'))
const InsuranceWizard = lazy(() => import('./pages/insurance/InsuranceWizard'))
const SubscriptionUpgradeWizard = lazy(() => import('./pages/profile/SubscriptionUpgradeWizard'))
const ApproveCertificationWizard = lazy(() => import('./pages/admin/ApproveCertificationWizard'))
const JoinDiveCenterWizard = lazy(() => import('./pages/divecenter/JoinDiveCenterWizard'))
const ReportIncidentWizard = lazy(() => import('./pages/safety/ReportIncidentWizard'))

function KycWizardRoute() {
  const navigate = useNavigate()
  return <KycWizardRaw onClose={() => navigate(-1)} />
}

function AnalyticsTracker() {
  useAnalytics()
  return null
}

const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
  </div>
)

export default function App() {
  return (
    <GlossaryProvider>
    <AnalyticsTracker />
    <ImpersonationBanner />
    <CookieBanner />
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/oauth/callback" element={<OAuthCallbackPage />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/app/onboarding" element={<OnboardingWizard />} />
          <Route path="/app/dashboard" element={<DashboardPage />} />
          <Route path="/app/gioco" element={<SpeciesQuizPage />} />
          <Route path="/app/profile" element={<ProfilePage />} />
          <Route path="/app/profile/certifications/:id/card" element={<CertCardPage />} />
          <Route path="/app/profile/subscription" element={<SubscriptionPage />} />
          <Route path="/app/profile/checkin-qr" element={<CheckInQRPage />} />
          <Route path="/app/profile/career" element={<CareerPage />} />
          <Route path="/app/profile/medical-history" element={<MedicalHistoryPage />} />
          <Route path="/app/profile/emergency-qr" element={<EmergencyQRPage />} />
          <Route path="/app/profile/cert-wallet" element={<CertWalletPage />} />
          <Route path="/app/logbook" element={<LogbookPage />} />
          <Route path="/app/logbook/analytics" element={<LogbookAnalyticsPage />} />
          <Route path="/app/logbook/trip-report" element={<TripReportPage />} />
          <Route path="/app/logbook/compare" element={<DiveComparePage />} />
          <Route path="/app/logbook/peer-compare" element={<Navigate to="/app/logbook/compare?mode=peer" replace />} />
          <Route path="/app/logbook/profile-insights" element={<ProfileAIInsightsPage />} />
          <Route path="/app/planning/ai-briefing" element={<AIDiveBriefingPage />} />
          <Route path="/app/planning/wizard"       element={<PlanDiveWizard />} />
          <Route path="/app/planning/tools"        element={<PlanningToolsPage />} />
          <Route path="/app/profile/wizard"       element={<ProfileSetupWizard />} />
          <Route path="/app/medical/wizard"        element={<MedicalWizard />} />
          <Route path="/app/medical/doctor-wizard"         element={<DoctorOnboardingWizard />} />
          <Route path="/app/divecenter/wizard"            element={<DiveCenterOnboardingWizard />} />
          <Route path="/app/instructor/wizard"            element={<InstructorOnboardingWizard />} />
          <Route path="/app/divemaster/wizard"            element={<DivemasterOnboardingWizard />} />
          <Route path="/app/agency/wizard"               element={<AgencyOnboardingWizard />} />
          <Route path="/app/associations/wizard"          element={<AssociationOnboardingWizard />} />
          <Route path="/app/academy/wizard"              element={<AcademyOnboardingWizard />} />
          <Route path="/app/photographer/wizard"         element={<PhotographerOnboardingWizard />} />
          <Route path="/app/research-center/wizard"      element={<ResearchCenterOnboardingWizard />} />
          <Route path="/app/logbook/log-wizard"          element={<LogDiveWizard />} />
          <Route path="/app/logbook/export-wizard"       element={<ExportLogbookWizard />} />
          <Route path="/app/courses/enroll-wizard"       element={<CourseEnrollWizard />} />
          <Route path="/app/bookings/wizard"             element={<BookDiveWizard />} />
          <Route path="/app/profile/cert-wizard"         element={<AddCertificationWizard />} />
          <Route path="/app/profile/gear-wizard"         element={<AddEquipmentWizard />} />
          <Route path="/app/social/buddy-wizard"         element={<FindBuddyWizard />} />
          <Route path="/app/biology-center/join-wizard"  element={<JoinMissionWizard />} />
          <Route path="/app/divecenter/create-excursion-wizard" element={<CreateExcursionWizard />} />
          <Route path="/app/divecenter/excursions/:id" element={<DCExcursionDetailPage />} />
          <Route path="/app/gdpr/privacy-wizard"         element={<PrivacyWizard />} />
          <Route path="/app/medical/assessment-wizard"   element={<MedicalAssessmentWizard />} />
          <Route path="/app/social/create-squad-wizard"  element={<CreateSquadWizard />} />
          <Route path="/app/trips/travel-wizard"         element={<TravelPackageWizard />} />
          <Route path="/app/diaries/create-wizard"       element={<CreateDiaryWizard />} />
          <Route path="/app/kyc/wizard"                  element={<KycWizardRoute />} />
          <Route path="/app/profile/request-role-wizard"   element={<RequestRoleWizard />} />
          <Route path="/app/divecenter/invite-staff-wizard" element={<InviteStaffWizard />} />
          <Route path="/app/divecenter/create-course-wizard" element={<CreateCourseWizard />} />
          <Route path="/app/safety/check-in-wizard"        element={<SafetyCheckInWizard />} />
          <Route path="/app/divecenter/issue-cert-wizard"  element={<IssueCertificateWizard />} />
          <Route path="/app/insurance/wizard"              element={<InsuranceWizard />} />
          <Route path="/app/profile/upgrade-wizard"        element={<SubscriptionUpgradeWizard />} />
          <Route path="/app/admin/approve-cert-wizard"     element={<ApproveCertificationWizard />} />
          <Route path="/app/divecenter/join-wizard"        element={<JoinDiveCenterWizard />} />
          <Route path="/app/safety/report-incident-wizard" element={<ReportIncidentWizard />} />
          <Route path="/app/species/contribute-wizard"    element={<ContributeSpeciesMediaWizard />} />
          <Route path="/app/species/bulk-upload"          element={<BulkUploadConsolePage />} />
          <Route path="/app/species/my-contributions"    element={<Navigate to="/app/species?tab=contributions" replace />} />
          <Route path="/app/logbook/computers" element={<DiveComputerDirectoryPage />} />
          <Route path="/app/logbook/gear-gas" element={<GasDecoPage />} />
          <Route path="/app/community/challenges" element={<ChallengesPage />} />
          <Route path="/app/logbook/import" element={<DiveImportWizard />} />
          <Route path="/app/logbook/prerequisites" element={<CertPrerequisitesPage />} />
          <Route path="/app/goals" element={<GoalsPage />} />
          <Route path="/app/logbook/gas-planner" element={<GasPlannerPage />} />
          <Route path="/app/logbook/:id" element={<DiveDetailPage />} />
          <Route path="/app/glossary" element={<GlossaryPage />} />
          <Route path="/app/divesites" element={<DivesitesPage />} />
          {/* U0 — pannello unico Anagrafica (Persone + Aziende in un solo workspace) */}
          <Route path="/app/anagrafica" element={<AnagraficaWorkspacePage />} />
          <Route path="/app/centers/search" element={<UnifiedDirectorySearchPage />} />
          <Route path="/app/party/organizations/:id" element={<PartyOrganizationDetailPage />} />
          <Route path="/app/persone/search" element={<PersonSearchPage />} />
          <Route path="/app/party/persons/by-user/:userId" element={<PartyPersonByUserPage />} />
          <Route path="/app/party/persons/:id" element={<PartyPersonDetailPage />} />
          <Route path="/app/divesites/mpa" element={<MPAZonesPage />} />
          <Route path="/app/wishlist" element={<WishlistPage />} />
          <Route path="/app/near-me" element={<NearMePage />} />
          <Route path="/app/recommendations" element={<RecommendationsPage />} />
          <Route path="/app/settings" element={<SettingsPage />} />
          <Route path="/app/settings/2fa" element={<TwoFactorPage />} />
          <Route path="/app/divecenter/safety-register" element={<SafetyRegisterPage />} />
          <Route path="/app/courses" element={<CoursesPage />} />
          <Route path="/app/courses/:id/learn" element={<CourseLessonsPage />} />
          <Route path="/app/courses/:id/exam" element={<ExamPage />} />
          <Route path="/app/courses/pss" element={<PSSExamPage />} />
          <Route path="/app/courses/pss/:code" element={<PSSExamPage />} />
          <Route path="/app/formazione" element={<FormazionePage />} />
          <Route path="/app/diving" element={<DivingHubPage />} />
          {/* Servizi (ambasciate, ospedali, noleggi...): le rotte statiche prima di :slug */}
          <Route path="/app/services" element={<ServicesHubPage />} />
          <Route path="/app/services/all" element={<ServicesCategoryPage />} />
          <Route path="/app/services/item/:id" element={<ServiceDetailPage />} />
          <Route path="/app/services/:slug" element={<ServicesCategoryPage />} />
          <Route path="/app/planning" element={<PlanningPage />} />
          <Route path="/app/events" element={<EventsPage />} />
          <Route path="/app/excursions" element={<ExcursionsPage />} />
          <Route path="/app/excursions/:id" element={<ExcursionDetailPage />} />
          <Route path="/app/qa" element={<QAPage />} />
          <Route path="/app/qa/:id" element={<QuestionPage />} />
          <Route path="/app/social" element={<SocialPage />} />
          <Route path="/app/live/upload" element={<ClipUploadPage />} />
          {/* Era raggiungibile da QUALSIASI utente autenticato: stava fuori dal blocco
              admin. Ora reindirizza al pannello, che il permesso ce l'ha. */}
          <Route path="/app/admin/clips" element={<Navigate to="/app/crm-marketing/clips" replace />} />
          <Route path="/app/chat" element={<ChatPage />} />
          <Route path="/app/bookings" element={<BookingsPage />} />
          <Route path="/app/insurance" element={<InsurancePage />} />
          <Route path="/app/photography" element={<PhotographyHubPage />} />
          <Route path="/app/media/correct" element={<MediaCorrectionPage />} />
          <Route path="/app/manuali" element={<ManualiPage />} />
          <Route path="/app/media" element={<MediaPage />} />
          <Route path="/app/medical" element={<MedicalPage />} />
          <Route path="/app/medical/questionnaire" element={<MedicalQuestionnairePage />} />
          <Route path="/app/medical/fitness" element={<FitnessAssessmentsPage />} />
          <Route path="/app/medical/find-doctor" element={<FindMedicalPage />} />
          <Route path="/app/equipment/cylinders" element={<CylinderCertificationsPage />} />
          <Route path="/app/dive-doctor" element={<DiveDoctorPage />} />
          {/* Le rotte di sistema (blocchi requireAdmin + requireSales) sono state
              spostate nel gruppo <ConsoleShell> più in basso: stessa area autenticata,
              guscio amministrativo separato da quello di prodotto (UX-01, Shell B). */}
          {/* PSS management — admin + instructor */}
          <Route element={<ProtectedRoute requireRole={['instructor', 'admin', 'dive_center', 'academy']} />}>
            <Route path="/app/admin/pss" element={<AdminPSSPage />} />
            <Route path="/app/admin/mpa" element={<AdminMpaPage />} />
          </Route>
          <Route element={<ProtectedRoute requireRole={['admin']} />}>
            <Route path="/app/admin/promotions" element={<Navigate to="/app/crm-marketing/promotions" replace />} />
          </Route>
          <Route path="/app/associations" element={<AssociationsPage />} />
          <Route path="/app/associations/members" element={<AssociationsPage />} />
          <Route path="/app/tourism" element={<TourismPage />} />
          <Route path="/app/tourism/excursions" element={<TourismExcursionsPage />} />
          <Route path="/app/marketplace" element={<MarketplacePage />} />
          <Route path="/app/merch" element={<MerchShopPage />} />
          <Route path="/app/calendar" element={<CalendarPage />} />
          <Route path="/app/content" element={<ContentPage />} />
          <Route path="/app/news" element={<NewsReaderPage />} />
          <Route path="/app/ai" element={<AIPage />} />
          <Route path="/app/analytics" element={<AnalyticsPage />} />
          <Route path="/app/associations/:id/shop" element={<AssociationShopPage />} />
          <Route path="/app/gdpr" element={<GDPRPage />} />
          <Route path="/app/referral" element={<ReferralPage />} />
          <Route path="/app/referral/network" element={<MyNetworkPage />} />
          {/* Come clips: stava fuori dal blocco admin, quindi aperto a ogni utente loggato. */}
          <Route path="/app/admin/referral-tree" element={<Navigate to="/app/crm-marketing/referral-tree" replace />} />
          <Route path="/app/achievements" element={<AchievementsPage />} />
          <Route path="/app/wallet" element={<WalletPage />} />
          <Route path="/app/safety" element={<SafetyPage />} />
          <Route path="/app/safety/checkin" element={<DiveCheckInPage />} />
          <Route path="/app/equipment" element={<Navigate to="/app/profile?tab=attrezzatura" replace />} />
          <Route path="/app/equipment/catalogue" element={<Navigate to="/app/profile?tab=attrezzatura" replace />} />
          <Route path="/app/my-gear" element={<Navigate to="/app/profile?tab=attrezzatura" replace />} />
          <Route path="/app/my-gear/:itemId" element={<Navigate to="/app/profile?tab=attrezzatura" replace />} />
          <Route path="/app/professional" element={<Navigate to="/app/profile?tab=professionale" replace />} />
          <Route path="/app/species" element={<UnifiedSpeciesPage />} />
          <Route path="/app/species/encyclopedia" element={<Navigate to="/app/species" replace />} />
          <Route path="/app/species/portal" element={<MarineBiologistPortal />} />
          <Route path="/app/biology-center/dashboard" element={<BiologyCenterDashboard />} />
          <Route path="/app/biology-center/missions" element={<MissionsPage />} />
          <Route path="/app/biology-center/missions/:id" element={<MissionDetailPage />} />
          <Route path="/app/biology-center/missions/:id/results" element={<MissionResultsPage />} />
          {/* Ricerca strutturata del servizio marine: non e la stessa cosa delle missioni,
              che sono scienza partecipata. Qui ci sono protocolli, squadra e validazione. */}
          <Route path="/app/biology-center/research" element={<MarineProjectsPage />} />
          <Route path="/app/biology-center/research/:id" element={<MarineProjectDetailPage />} />
          <Route path="/app/biology-center/research/:id/dives/:diveId" element={<MarineDiveDetailPage />} />
          <Route path="/app/biology-center/alerts" element={<AlertsPage />} />
          <Route path="/app/biology-center/species-editor" element={<SpeciesEditorPage />} />

          <Route path="/app/biology-center/analytics" element={<ResearchAnalyticsPage />} />
          <Route path="/app/biology-center/taxonomy" element={<TaxonomyExplorerPage />} />
          <Route path="/app/biology-center/assistant" element={<MarineBioAssistantPage />} />
          <Route path="/app/biology-center/identifier" element={<SpeciesIdentifierPage />} />
          <Route path="/app/biology-center" element={<BioCenterHubPage />} />
          <Route path="/app/marine/gallery"       element={<MarineGalleryPage />} />
          <Route path="/app/marine/gallery/:id"   element={<MarineMediaDetailPage />} />

          <Route path="/app/biology-center/journal" element={<FieldJournalPage />} />
          <Route path="/app/biology-center/report" element={<SpeciesReportPage />} />
          <Route path="/app/photographer/portal" element={<PhotographerPortalPage />} />
          <Route path="/app/professionals" element={<ProfessionalsDirectoryPage />} />
          <Route element={<ProtectedRoute requireProfileType={['INSTRUCTOR', 'DIVEMASTER']} />}>
            <Route path="/app/instructor" element={<InstructorDashboardPage />} />
          </Route>
          <Route path="/app/divemaster/productivity" element={<DivemasterProductivityPage />} />
          <Route path="/app/academy" element={<AcademyDashboardPage />} />
          <Route path="/app/research-center" element={<ResearchCenterPage />} />
          <Route path="/app/instructor/directory" element={<InstructorDirectoryPage />} />
          <Route path="/app/directory/sites" element={<DiveSiteDirectoryPage />} />
          <Route path="/app/instructor/:userId" element={<InstructorPublicProfilePage />} />
          <Route path="/app/divers/:userId" element={<PublicDiverProfilePage />} />
          <Route path="/app/contacts" element={<ContactsPage />} />
          <Route path="/app/contacts/requests" element={<ContactRequestsPage />} />
          <Route path="/app/my-courses" element={<StudentCoursesPage />} />
          <Route path="/app/my-lessons" element={<PrivateLessonsPage role="diver" />} />
          <Route path="/app/my-certifications" element={<MyCertificationsPage />} />
          <Route path="/app/my-staff-requests" element={<MyStaffRequestsPage />} />
          <Route path="/app/my-medcerts" element={<DCMedCertRequestsPage />} />
          <Route path="/app/jobs" element={<JobBoardPage />} />
          <Route path="/app/notifications" element={<NotificationsPage />} />
          <Route path="/app/community" element={<CommunityPage />} />
          <Route path="/app/community/polls" element={<PollsPage />} />
          <Route path="/app/ai/advisor" element={<AdvisorPage />} />
          <Route path="/app/social/leaderboard" element={<LeaderboardPage />} />
          <Route path="/app/social/buddy-matches" element={<BuddyMatchesPage />} />
          <Route path="/app/social/squads" element={<DiveSquadsPage />} />
          <Route path="/app/search" element={<SearchPage />} />
          <Route path="/app/developer" element={<DeveloperPage />} />
          <Route path="/app/data-lab" element={<DataLabPage />} />
          <Route path="/app/centers" element={<UnifiedDiveCenterPage />} />
          <Route path="/app/centers/:id" element={<DiveCenterProfilePage />} />
          <Route path="/app/trips" element={<TripPlannerPage />} />
          <Route path="/app/trips/accept/:token" element={<AcceptInvitePage />} />
          <Route path="/app/sport-centers" element={<SportCentersPage />} />
          <Route path="/app/sport-centers/:id" element={<SportCenterDetailPage />} />
          <Route path="/app/my-sport-center" element={<MySportCenterPage />} />
          <Route path="/app/diaries" element={<TravelDiariesPage />} />
          <Route path="/app/diaries/:id" element={<DiaryDetailPage />} />
          <Route path="/app/diaries/invite/:token" element={<DiaryInvitePage />} />
          <Route path="/app/apnea" element={<ApneaPage />} />
          <Route path="/app/diver/training" element={<DiverTrainingPage />} />
          <Route path="/app/doctor" element={<DoctorPage />} />
          <Route path="/app/install" element={<InstallPage />} />

          {/* Travel Agency management */}
          <Route path="/app/agency" element={<MyAgencyPage />} />
          <Route path="/app/agency/packages" element={<AgencyPackagesPage />} />
          <Route path="/app/agency/bookings" element={<AgencyBookingsPage />} />
          <Route path="/app/agency/my-bookings" element={<MyTravelBookingsPage />} />

          {/* Charter operator */}
          <Route path="/app/charter/my-charter" element={<MyCharterPage />} />

          {/* Doctor profile & assessments */}
          <Route path="/app/medical/profile" element={<DoctorProfilePage />} />

          {/* Pre-dive authorization form */}
          <Route path="/app/dive-authorization" element={<DiveAuthorizationPage />} />

          {/* Dive Center management routes */}
          <Route element={<ProtectedRoute requireProfileType={['DIVE_CENTER']} />}>
            <Route path="/app/divecenter" element={<DiveCenterLayout />}>
              <Route index element={<DCDashboardPage />} />
              <Route path="bookings" element={<DCBookingsPage />} />
              <Route path="customers" element={<DCCustomersPage />} />
              <Route path="planning" element={<DCPlanningPage />} />
              <Route path="calendar" element={<DCCalendarPage />} />
              <Route path="equipment" element={<DCEquipmentPage />} />
              <Route path="operations" element={<DCOperationsPage />} />
              <Route path="logbook" element={<DCLogbookPage />} />
              <Route path="courses" element={<DCCoursesPage />} />
              <Route path="calendar/:entryId/sheet" element={<DCBoatSheetPage />} />
              <Route path="courses/:courseId/team" element={<DCCourseTeamPage />} />
              <Route path="courses/:courseId/enrollments/:enrollmentId/progress" element={<DCStudentProgressPage />} />
              <Route path="waivers/new" element={<DCWaiverSignPage />} />
              <Route path="admin" element={<DCAdminPage />} />
              <Route path="marketing" element={<DCMarketingPage />} />
              <Route path="rental" element={<DCRentalPage />} />
              <Route path="widget" element={<DCWidgetPage />} />
              <Route path="compliance" element={<DCCompliancePage />} />
              <Route path="customer-notes" element={<DCCustomerNotesPage />} />
              <Route path="hr" element={<DCHRPage />} />
              <Route path="reports" element={<DCReportsPage />} />
              <Route path="briefings" element={<BriefingCreatorPage />} />
              <Route path="invoices" element={<DCInvoicesPage />} />
              <Route path="pricing" element={<DCPricingPage />} />
              <Route path="shifts" element={<DCShiftsPage {...{} as any} />} />
              <Route path="excursions" element={<DCExcursionsPage />} />
              <Route path="boats" element={<DCBoatsPage />} />
              <Route path="equipment-maintenance" element={<DCEquipmentMaintenancePage />} />
              <Route path="equipment-maintenance/:itemId" element={<DCEquipmentItemDetailPage />} />
              <Route path="analytics" element={<DCAnalyticsPage />} />
            </Route>
          </Route>
        </Route>

        {/* ══ Console di sistema (Shell B) ══════════════════════════════════════
            Le rotte amministrative e il pannello CRM & Marketing vivono in un guscio
            dedicato (ConsoleShell), fuori dall'AppLayout utente: niente nav di prodotto,
            FAB, onboarding o banner profilo. Stesse URL (/app/admin/*, /app/crm-marketing/*)
            e stessi guard (requireAdmin / requireSales): cambia solo il guscio. */}
        <Route element={<ConsoleShell />}>
          {/* Admin-only routes */}
          <Route element={<ProtectedRoute requireAdmin />}>
            <Route path="/app/admin/hub" element={<AdminHubPage />} />
            <Route path="/app/admin" element={<AdminPage />} />
            <Route path="/app/admin/users" element={<AdminUsersPage />} />
            <Route path="/app/admin/species" element={<Navigate to="/app/species?tab=admin" replace />} />
            <Route path="/app/admin/species-curation" element={<Navigate to="/app/species?tab=admin" replace />} />
            <Route path="/app/admin/inat-staging" element={<Navigate to="/app/species?tab=admin" replace />} />
            <Route path="/app/admin/whatsapp" element={<WhatsAppAdminPage />} />
            <Route path="/app/admin/centers" element={<Navigate to="/app/centers?tab=admin" replace />} />
            <Route path="/app/admin/gdpr-sagas" element={<AdminGdprSagasPage />} />
            <Route path="/app/admin/gdpr-processing-records" element={<AdminGdprProcessingRecordsPage />} />
            <Route path="/app/admin/gdpr-retention" element={<AdminGdprRetentionPage />} />
            <Route path="/app/admin/jurisdiction-rules" element={<AdminJurisdictionRulesPage />} />
            {/* Console delle comunicazioni: otto rotte del servizio comms senza interfaccia. */}
            <Route path="/app/admin/comms" element={<AdminCommsPage />} />
            <Route path="/app/admin/content-pages" element={<AdminContentPagesPage />} />
            <Route path="/app/admin/rbac-policy" element={<AdminRbacPolicyPage />} />
            <Route path="/app/admin/rules" element={<AdminRulesPage />} />
            <Route path="/app/admin/badge-visibility" element={<BadgeVisibilityPage />} />
            <Route path="/app/admin/data-lab" element={<AdminDataLabPage />} />
            <Route path="/app/admin/config/inat-token" element={<AdminInatTokenPage />} />
            <Route path="/app/admin/integrations" element={<AdminIntegrationsPage />} />
            <Route path="/app/admin/services" element={<AdminServicesPage />} />
            <Route path="/app/admin/role-requests" element={<AdminRoleRequestsPage />} />
            <Route path="/app/admin/doctor-verification" element={<AdminDoctorVerificationPage />} />
            <Route path="/app/admin/tour-operators" element={<AdminTourOperatorsPage />} />
            <Route path="/app/admin/certifications" element={<AdminCertificationsPage />} />
            <Route path="/app/admin/stats" element={<AdminStatsPage />} />
            {/* Party */}
            <Route path="/app/admin/party" element={<AdminPartyPage />} />
            {/* CRM, campagne e contenuti sono usciti da qui: vivono in /app/crm-marketing,
                che ha un permesso proprio (ruolo `sales` oltre agli amministratori). Restano
                solo i reindirizzamenti, perché questi indirizzi sono nei segnalibri di chi
                lavora e in link sparsi per l'applicazione. */}
            <Route path="/app/admin/crm/contacts" element={<Navigate to="/app/anagrafica?tab=persone" replace />} />
            <Route path="/app/admin/crm/contacts/new"      element={<Navigate to="/app/crm-marketing/crm/contacts/new" replace />} />
            <Route path="/app/admin/crm/contacts/:id"      element={<RedirectContact />} />
            <Route path="/app/admin/crm/contacts/:id/edit" element={<RedirectContact edit />} />
            <Route path="/app/admin/crm/agenda"     element={<Navigate to="/app/crm-marketing/crm/agenda" replace />} />
            <Route path="/app/admin/crm/todos"      element={<Navigate to="/app/crm-marketing/crm/todos" replace />} />
            <Route path="/app/admin/crm/import-osm" element={<Navigate to="/app/crm-marketing/crm/import-osm" replace />} />
            <Route path="/app/admin/crm/enrich"     element={<Navigate to="/app/crm-marketing/crm/enrich" replace />} />
            <Route path="/app/admin/crm/outreach"   element={<Navigate to="/app/crm-marketing/crm/outreach" replace />} />
            <Route path="/app/admin/crm"            element={<Navigate to="/app/crm-marketing/crm" replace />} />
            <Route path="/app/admin/marketing"      element={<Navigate to="/app/crm-marketing/marketing" replace />} />
            <Route path="/app/admin/emails"         element={<Navigate to="/app/crm-marketing/emails" replace />} />
            <Route path="/app/admin/news"           element={<Navigate to="/app/crm-marketing/news" replace />} />
            <Route path="/app/admin/challenges"     element={<Navigate to="/app/crm-marketing/challenges" replace />} />
            {/* Directory — F4 consolidamento: punto unico = /app/centers/search (Party).
                diving-centers resta come pannello import/catalogo OSM (fino a F5); le altre
                ex-directory redirigono al punto unico pre-filtrato (no 404 sui bookmark). */}
            <Route path="/app/admin/directory/diving-centers"   element={<DirectoryPage category="diving-centers" />} />
            <Route path="/app/admin/directory/hospitals"        element={<Navigate to="/app/centers/search?type=HOSPITAL" replace />} />
            <Route path="/app/admin/directory/research-centers" element={<Navigate to="/app/centers/search?type=RESEARCH_CENTER" replace />} />
            <Route path="/app/admin/directory/pools"            element={<Navigate to="/app/centers/search?type=POOL" replace />} />
            <Route path="/app/admin/directory/car-rentals"      element={<Navigate to="/app/centers/search?type=CAR_RENTAL" replace />} />
            <Route path="/app/admin/directory/boat-rentals"     element={<Navigate to="/app/centers/search?type=BOAT_RENTAL" replace />} />
            <Route path="/app/admin/directory/instructors"      element={<Navigate to="/app/persone/search?role=INSTRUCTOR" replace />} />
            <Route path="/app/admin/directory/doctors"          element={<Navigate to="/app/persone/search?role=DIVE_DOCTOR" replace />} />
            <Route path="/app/admin/mail" element={<Navigate to="/app/crm-marketing/mail" replace />} />
            <Route path="/app/admin/merch" element={<AdminMerchPage />} />
            <Route path="/app/admin/equipment-catalogue" element={<AdminEquipmentCataloguePage />} />
            <Route path="/app/admin/equipment-overdue" element={<AdminEquipmentOverduePage />} />
          </Route>
          {/* ── Pannello CRM & Marketing ──────────────────────────────────────────
              Vi accede il ruolo `sales`, oltre agli amministratori (isSalesUser è già vero
              per gli admin). Il permesso vero sta nei servizi: questo blocco decide cosa si
              vede, non cosa si può fare. */}
          <Route element={<ProtectedRoute requireSales />}>
            <Route path="/app/crm-marketing" element={<CrmMarketingHubPage />} />
            {/* CRM */}
            <Route path="/app/crm-marketing/crm" element={<CrmDashboardPage />} />
            {/* La lista contatti è l'Anagrafica Unica: il CRM è una vista su quel registro,
                non un archivio parallelo. Le schede operative restano su /contacts/:id. */}
            <Route path="/app/crm-marketing/crm/contacts" element={<Navigate to="/app/anagrafica?tab=persone" replace />} />
            <Route path="/app/crm-marketing/crm/contacts/new" element={<CrmContactFormPage />} />
            <Route path="/app/crm-marketing/crm/contacts/:id" element={<CrmContactDetailPage />} />
            <Route path="/app/crm-marketing/crm/contacts/:id/edit" element={<CrmContactFormPage />} />
            <Route path="/app/crm-marketing/crm/agenda" element={<CrmAgendaPage />} />
            <Route path="/app/crm-marketing/crm/todos" element={<CrmTodosPage />} />
            <Route path="/app/crm-marketing/crm/import-osm" element={<CrmOsmImportPage />} />
            <Route path="/app/crm-marketing/crm/enrich" element={<CrmEnrichPage />} />
            {/* Outreach WhatsApp: lista di lavoro click-to-chat per una campagna.
                Non c'entra con /app/admin/whatsapp, che configura le API Business (mai attivate). */}
            <Route path="/app/crm-marketing/crm/outreach" element={<CrmOutreachPage />} />
            <Route path="/app/crm-marketing/referral-tree" element={<AdminReferralTreePage />} />
            {/* Campagne e contatto diretto */}
            <Route path="/app/crm-marketing/marketing" element={<MarketingPage />} />
            <Route path="/app/crm-marketing/emails" element={<AdminEmailPage />} />
            <Route path="/app/crm-marketing/mail" element={<MailPage />} />
            <Route path="/app/crm-marketing/promotions" element={<AdminPromotionsPage />} />
            {/* Contenuti e community */}
            <Route path="/app/crm-marketing/news" element={<AdminNewsPage />} />
            <Route path="/app/crm-marketing/challenges" element={<AdminChallengesPage />} />
            <Route path="/app/crm-marketing/clips" element={<AdminClipsPage />} />
          </Route>
        </Route>
      </Route>

      {/* W5 — pagine PUBBLICHE crawlabili del data-moat (SEO globale, no auth) */}
      <Route path="/immersioni" element={<PublicDiveSitesPage />} />
      <Route path="/immersioni/:id" element={<PublicDiveSiteDetailPage />} />
      <Route path="/specie" element={<PublicSpeciesPage />} />
      <Route path="/specie/:id" element={<PublicSpeciesDetailPage />} />
      <Route path="/centri" element={<PublicCentersPage />} />
      <Route path="/centri/:id" element={<PublicCenterDetailPage />} />
      <Route path="/gioco" element={<SpeciesQuizPage />} />
      <Route path="/blog/:id" element={<PublicArticlePage />} />

      <Route element={<PublicLayout />}>
        <Route path="/funzionalita" element={<FunzionalitaPage />} />
        <Route path="/per-istruttori" element={<PerIstruttoriPage />} />
        <Route path="/per-centri" element={<PerCentriPage />} />
        {/* Landing della campagna verso le scuole del Lazio: percorso corto perche' viene
            dettato al telefono. Pubblica, nessuna registrazione richiesta. */}
        <Route path="/lazio" element={<CampagnaLazioPage />} />
        <Route path="/prezzi" element={<PrezziPage />} />
        <Route path="/chi-siamo" element={<ChiSiamoPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/partner" element={<PartnerPage />} />
        <Route path="/contatti" element={<ContattiPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/termini-di-servizio" element={<TerminiServizioPage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/gdpr-info" element={<GdprInfoPage />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
      </Route>

      {/* GDPR unsubscribe — public, no auth */}
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
      <Route path="/unsub/:token"       element={<UnsubscribePage />} />

      {/* Marketing consent result pages — no auth required */}
      <Route path="/consent-confirmed" element={<ConsentResultPage />} />
      <Route path="/consent-revoked" element={<ConsentResultPage />} />
      <Route path="/consent-error" element={<ConsentResultPage />} />

      {/* Public briefing share page — no auth required */}
      <Route path="/briefing/:token" element={<BriefingSharePage />} />

      {/* Public event invitation acceptance — no auth required */}
      <Route path="/events/invitation/:token" element={<EventInvitationPage />} />

      {/* Public dive share page — no auth required */}
      <Route element={<PublicLayout />}>
        <Route path="/dives/:id" element={<PublicDivePage />} />
      </Route>

      {/* Public dive share page (per-dive share link) — no auth required */}
      <Route path="/share/dive/:diveId" element={<DiveSharePage />} />
      <Route path="/share/dive/t/:token" element={<DiveSharePage />} />

      {/* Public diary share page — no auth required */}
      <Route path="/diaries/share/:token" element={<DiaryPublicPage />} />

      {/* Public QR verify pages — no auth required */}
      <Route path="/verify/emergency-medical/:token" element={<EmergencyMedicalVerifyPage />} />
      <Route path="/verify/cert-wallet/:token" element={<CertWalletVerifyPage />} />
      <Route path="/verify/cert/:token" element={<CertVerifyPage />} />

      {/* Email verification — no auth required */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      <Route path="/" element={<LandingPage />} />
      <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
    </Routes>
    </Suspense>
    </GlossaryProvider>
  )
}
